import { ReportSdk } from 'lib-klab-sdks';
import {
    AdminTimesheetItem,
    AdminTimesheetResponse,
    AdminTimesheetResponseMetadata,
    AdminTimesheetPagination,
} from '../../../domain/models/reports/admin-timesheet-response.model';
import {
    AdminDownloadItem,
    AdminDownloadResponse,
    AdminDownloadResponseMetadata,
    AdminDownloadPagination,
} from '../../../domain/models/reports/admin-download-response.model';
import {
    AdminDownloadEmployeeItem,
    AdminDownloadEmployeeResponse,
    AdminDownloadEmployeeResponseMetadata,
} from '../../../domain/models/reports/admin-download-employee-response.model';
import {
    EmployeeProductivityMetricItem,
    EmployeeProductivityMetricResponse,
} from '../../../domain/models/reports/employee-productivity-metric-response.model';
import {
    LocationProductivityMetricItem,
    LocationProductivityMetricResponse,
} from '../../../domain/models/reports/location-productivity-metric-response.model';
import {
    EmployeeProductivityItem,
    EmployeeProductivityResponse,
    EmployeeProductivityResponseMetadata,
    EmployeeProductivityPagination,
} from '../../../domain/models/reports/employee-productivity-response.model';
import {
    OrganizationProductivityItem,
    OrganizationProductivityResponse,
    OrganizationProductivityResponseMetadata,
    OrganizationProductivityPagination,
} from '../../../domain/models/reports/organization-productivity-response.model';
import { DateRange } from '../../../domain/models/reports/request/time-sheet-summary.request';
import { EmployeeDailyMetricItem, EmployeeDailyMetricResponse } from '../../../domain/models/reports/employee-daily-metric-response.model';

export function mapSdkDateRangeToDomain(dr?: ReportSdk.DateRange): DateRange | undefined {
    if (!dr) return undefined;
    return { startDate: dr.startDate, endDate: dr.endDate };
}

export function mapSdkAdminTimesheetMetadataToDomain(
    metadata?: ReportSdk.AdminTimesheetResponseMetadata,
): AdminTimesheetResponseMetadata {
    return {
        locationId: metadata?.locationId,
        departmentId: metadata?.departmentId,
        search: metadata?.search,
        dateRange: mapSdkDateRangeToDomain(metadata?.dateRange),
    };
}

export function mapSdkAdminTimesheetPaginationToDomain(
    page?: ReportSdk.PaginationResponse,
): AdminTimesheetPagination {
    return {
        page: page?.page ?? 0,
        size: page?.size ?? 0,
        totalElements: page?.totalElements ?? 0,
        totalPages: page?.totalPages ?? 0,
    };
}

export function mapSdkAdminTimesheetItemToDomain(
    item: ReportSdk.AdminTimesheetItem,
): AdminTimesheetItem {
    return {
        employee: item.employee
            ? {
                employeeId: item.employee.employeeId,
                code: item.employee.code ?? '',
                firstName: item.employee.firstName ?? '',
                lastName: item.employee.lastName ?? '',
            }
            : { employeeId: 0, code: '', firstName: '', lastName: '' },
        location: item.location
            ? { locationId: item.location.locationId, name: item.location.name ?? '' }
            : { locationId: 0, name: '' },
        department: item.department
            ? { departmentId: item.department.departmentId, name: item.department.name ?? '' }
            : { departmentId: 0, name: '' },
        date: item.date ?? '',
        startTime: item.startTime ?? '',
        endTime: item.endTime ?? '',
        totalSeconds: item.totalSeconds ?? 0,
        officeSeconds: item.officeSeconds ?? 0,
        activeSeconds: item.activeSeconds ?? 0,
        productiveSeconds: item.productiveSeconds ?? 0,
        unproductiveSeconds: item.unproductiveSeconds ?? 0,
        neutralSeconds: item.neutralSeconds ?? 0,
        idleSeconds: item.idleSeconds ?? 0,
        offlineSeconds: item.offlineSeconds ?? 0,
        // Se propaga tal cual, incluido el null: null = no medido, distinto de 0.
        // Sin esta línea la columna "Horas sin teclear" nunca recibía el dato y
        // mostraba "—" siempre, indistinguible de "no se midió".
        typelessSeconds: item.typelessSeconds ?? null,
        holiday: item.holiday ?? false,
        // El motivo se propaga tal cual: es lo que deja a la vista decir
        // "No laborable" en lugar de un guion que significa tres cosas.
        noExpectedReason: item.noExpectedReason ?? null,
        // Se propaga tal cual, incluido el null: sin jornada esperada (feriado,
        // día no agendado) el porcentaje es indefinido, no 0. Colapsarlo acá
        // borraba la distinción antes de que la vista pudiera mostrarla.
        productivityPercentage: item.productivityPercentage ?? null,
    };
}

export function mapSdkAdminTimesheetResponseToDomain(
    resp: ReportSdk.AdminTimesheetResponse,
): AdminTimesheetResponse {
    return {
        metadata: mapSdkAdminTimesheetMetadataToDomain(resp?.metadata),
        content: (resp?.content ?? []).map(mapSdkAdminTimesheetItemToDomain),
        page: mapSdkAdminTimesheetPaginationToDomain(resp?.page),
    };
}

export function mapSdkAdminDownloadMetadataToDomain(
    metadata?: ReportSdk.AdminDownloadResponseMetadata,
): AdminDownloadResponseMetadata {
    return {
        locationId: metadata?.locationId,
        departmentId: metadata?.departmentId,
        roleId: metadata?.roleId,
        search: metadata?.search,
        dateRange: mapSdkDateRangeToDomain(metadata?.dateRange),
    };
}

export function mapSdkAdminDownloadPaginationToDomain(
    page?: ReportSdk.PaginationResponse,
): AdminDownloadPagination {
    return {
        page: page?.page ?? 0,
        size: page?.size ?? 0,
        totalElements: page?.totalElements ?? 0,
        totalPages: page?.totalPages ?? 0,
    };
}

export function mapSdkAdminDownloadItemToDomain(
    item: ReportSdk.AdminDownloadItem,
): AdminDownloadItem {
    return {
        employee: item.employee
            ? {
                employeeId: item.employee.employeeId,
                code: item.employee.code ?? '',
                firstName: item.employee.firstName ?? '',
                lastName: item.employee.lastName ?? '',
                email: item.employee.email ?? '',
            }
            : { employeeId: 0, code: '', firstName: '', lastName: '', email: '' },
        location: item.location
            ? { locationId: item.location.locationId, name: item.location.name ?? '' }
            : { locationId: 0, name: '' },
        department: item.department
            ? { departmentId: item.department.departmentId, name: item.department.name ?? '' }
            : { departmentId: 0, name: '' },
        role: item.role
            ? { roleId: item.role.roleId, name: item.role.name ?? '' }
            : { roleId: 0, name: '' },
        startAt: item.startAt ?? '',
        endedAt: item.endedAt ?? '',
        totalSeconds: item.totalSeconds ?? 0,
        officeSeconds: item.officeSeconds ?? 0,
        activeSeconds: item.activeSeconds ?? 0,
        productiveSeconds: item.productiveSeconds ?? 0,
        unproductiveSeconds: item.unproductiveSeconds ?? 0,
        neutralSeconds: item.neutralSeconds ?? 0,
        idleSeconds: item.idleSeconds ?? 0,
        offlineSeconds: item.offlineSeconds ?? 0,
        totalWorkDaySeconds: item.totalWorkDaySeconds ?? 0,
        productivityPercentage: item.productivityPercentage ?? null,

    };
}

export function mapSdkAdminDownloadResponseToDomain(
    resp: ReportSdk.AdminDownloadResponse,
): AdminDownloadResponse {
    return {
        metadata: mapSdkAdminDownloadMetadataToDomain(resp?.metadata),
        content: (resp?.content ?? []).map(mapSdkAdminDownloadItemToDomain),
        page: mapSdkAdminDownloadPaginationToDomain(resp?.page),
    };
}

function mapSdkProductivityMetadataToDomain(
    metadata?: ReportSdk.AdminTimesheetResponseMetadata | null,
): EmployeeProductivityResponseMetadata & OrganizationProductivityResponseMetadata {
    return {
        dateRange: mapSdkDateRangeToDomain(metadata?.dateRange),
        locationId: metadata?.locationId,
        departmentId: metadata?.departmentId,
        search: metadata?.search,
    };
}

function mapSdkProductivityPaginationToDomain(
    page?: ReportSdk.PaginationResponse,
): EmployeeProductivityPagination & OrganizationProductivityPagination {
    return {
        page: page?.page ?? 0,
        size: page?.size ?? 0,
        totalElements: page?.totalElements ?? 0,
        totalPages: page?.totalPages ?? 0,
    };
}

export function mapSdkEmployeeProductivityItemToDomain(
    item: ReportSdk.EmployeeProductivityItem,
): EmployeeProductivityItem {
    return {
        employee: item.employee
            ? {
                employeeId: item.employee.employeeId ?? 0,
                firstName: item.employee.firstName ?? '',
                lastName: item.employee.lastName ?? '',
            }
            : { employeeId: 0, firstName: '', lastName: '' },
        startDate: item.startDate ?? '',
        endDate: item.endDate ?? '',
        officeSeconds: item.officeSeconds ?? 0,
        productiveSeconds: item.productiveSeconds ?? 0,
        holidayProductiveSeconds: item.holidayProductiveSeconds ?? null,
        productivityPercentage: item.productivityPercentage ?? null,

        unproductiveSeconds: item.unproductiveSeconds ?? 0,
        unproductivePercentage: item.unproductivePercentage ?? null,
        neutralSeconds: item.neutralSeconds ?? 0,
        idleSeconds: item.idleSeconds ?? 0,
        expectedSeconds: item.expectedSeconds ?? undefined,
    };
}
export function mapSdkEmployeeDailyMetricItemToDomain(
    item: ReportSdk.EmployeeDailyMetricItem,
): EmployeeDailyMetricItem {
    return {
        date:                 item.date                 ?? '',
        productiveSeconds:    item.productiveSeconds    ?? 0,
        unproductiveSeconds:  item.unproductiveSeconds  ?? 0,
        neutralSeconds:       item.neutralSeconds       ?? 0,
        idleSeconds:          item.idleSeconds          ?? 0,
    };
}

export function mapSdkEmployeeDailyMetricResponseToDomain(
    resp: ReportSdk.EmployeeDailyMetricResponse,
): EmployeeDailyMetricResponse {
    return {
        metrics: (resp?.metrics ?? []).map(mapSdkEmployeeDailyMetricItemToDomain),
    };
}

export function mapSdkEmployeeProductivityResponseToDomain(
    resp: ReportSdk.EmployeeProductivityResponse,
): EmployeeProductivityResponse {
    return {
        metadata: mapSdkProductivityMetadataToDomain(resp?.metadata),
        content: (resp?.content ?? []).map(mapSdkEmployeeProductivityItemToDomain),
        page: mapSdkProductivityPaginationToDomain(resp?.page),
    };
}

export function mapSdkOrganizationProductivityItemToDomain(
    item: ReportSdk.OrganizationProductivityItem,
): OrganizationProductivityItem {
    return {
        location: item.location
            ? {
                locationId: item.location.locationId ?? 0,
                name: item.location.name ?? '',
            }
            : { locationId: 0, name: '' },
        department: item.department
            ? {
                departmentId: item.department.departmentId,
                name: item.department.name,
            }
            : undefined,
        startDate: item.startDate ?? '',
        endDate: item.endDate ?? '',
        officeSeconds: item.officeSeconds ?? 0,
        productiveSeconds: item.productiveSeconds ?? 0,
        holidayProductiveSeconds: item.holidayProductiveSeconds ?? null,
        productivityPercentage: item.productivityPercentage ?? null,

        unproductiveSeconds: item.unproductiveSeconds ?? 0,
        unproductivePercentage: item.unproductivePercentage ?? null,
        neutralSeconds: item.neutralSeconds ?? 0,
        idleSeconds: item.idleSeconds ?? 0,
    };
}

export function mapSdkOrganizationProductivityResponseToDomain(
    resp: ReportSdk.OrganizationProductivityResponse,
): OrganizationProductivityResponse {
    return {
        metadata: mapSdkProductivityMetadataToDomain(resp?.metadata),
        content: (resp?.content ?? []).map(mapSdkOrganizationProductivityItemToDomain),
        page: mapSdkProductivityPaginationToDomain(resp?.page),
    };
}

export function mapSdkAdminDownloadEmployeeMetadataToDomain(
    metadata?: ReportSdk.AdminDownloadEmployeeResponseMetadata | null,
): AdminDownloadEmployeeResponseMetadata {
    return {
        employeeId: metadata?.employeeId,
        search: metadata?.search,
        dateRange: mapSdkDateRangeToDomain(metadata?.dateRange),
    };
}

export function mapSdkAdminDownloadEmployeeItemToDomain(
    item: ReportSdk.AdminDownloadEmployeeItem,
): AdminDownloadEmployeeItem {
    return {
        name: item.name ?? '',
        title: item.title ?? '',
        startAt: item.startAt ?? '',
        endedAt: item.endedAt ?? '',
        activeSeconds: item.activeSeconds ?? 0,
        idleSeconds: item.idleSeconds ?? 0,
        totalSeconds: item.totalSeconds ?? 0,
    };
}

export function mapSdkAdminDownloadEmployeeResponseToDomain(
    resp: ReportSdk.AdminDownloadEmployeeResponse,
): AdminDownloadEmployeeResponse {
    return {
        metadata: mapSdkAdminDownloadEmployeeMetadataToDomain(resp?.metadata),
        content: (resp?.content ?? []).map(mapSdkAdminDownloadEmployeeItemToDomain),
        page: mapSdkAdminDownloadPaginationToDomain(resp?.page),
    };
}

export function mapSdkEmployeeProductivityMetricItemToDomain(
    item: ReportSdk.EmployeeProductivityMetricItem,
): EmployeeProductivityMetricItem {
    return {
        department: item.department,
        name: item.name,
        productiveSeconds: item.productiveSeconds,
        unproductiveSeconds: item.unproductiveSeconds,
        neutralSeconds: item.neutralSeconds,
        idleSeconds: item.idleSeconds,
    };
}

export function mapSdkEmployeeProductivityMetricResponseToDomain(
    resp: ReportSdk.EmployeeProductivityMetricResponse,
): EmployeeProductivityMetricResponse {
    return {
        metrics: (resp?.metrics ?? []).map(mapSdkEmployeeProductivityMetricItemToDomain),
    };
}

export function mapSdkLocationProductivityMetricItemToDomain(
    item: ReportSdk.LocationProductivityMetricItem,
): LocationProductivityMetricItem {
    return {
        location: item.location,
        department: item.department,
        productiveSeconds: item.productiveSeconds,
        unproductiveSeconds: item.unproductiveSeconds,
        neutralSeconds: item.neutralSeconds,
        idleSeconds: item.idleSeconds,
    };
}

export function mapSdkLocationProductivityMetricResponseToDomain(
    resp: ReportSdk.LocationProductivityMetricResponse,
): LocationProductivityMetricResponse {
    return {
        metrics: (resp?.metrics ?? []).map(mapSdkLocationProductivityMetricItemToDomain),
    };
}
